import { useCallback, useEffect, useState } from 'react'

import {
    fetchCompanyBootstrap,
    loginToBackend,
    readMicrosoftTokenClaims,
    readSessionClaims,
    type SessionClaims,
} from '../api'
import { sharedAccountManager } from '../authConfig'
import { createDiagnosticID, diagnosticContext, diagnosticError, logDiagnostic } from '../diagnostics'
import { clientId, outlookBackendScopes } from '../msalconfig'

import type { OfficeOption } from '../models/rooms'

const accountManager = sharedAccountManager

function mapBootstrapOffices(bootstrap: Awaited<ReturnType<typeof fetchCompanyBootstrap>>): OfficeOption[] {
    return (bootstrap.company.Offices?.items || [])
        .filter((office): office is NonNullable<typeof office> => Boolean(office?.id && office.name))
        .map((office) => ({
            id: office.id,
            name: office.name,
            hours: `${office.workingFrom || '08:00'} - ${office.workingTo || '18:00'}`,
            timeZone: office.timeZone || 'Europe/Berlin',
        }))
}

export function useTaskpaneSession() {
    const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading')
    const [authMessage, setAuthMessage] = useState('Authorizing your Microsoft account...')
    const [sessionClaims, setSessionClaims] = useState<SessionClaims | null>(null)
    const [microsoftTenantId, setMicrosoftTenantId] = useState('')
    const [sessionToken, setSessionToken] = useState('')
    const [offices, setOffices] = useState<OfficeOption[]>([])
    const [initialOfficeId, setInitialOfficeId] = useState('')

    const authorizeUser = useCallback(async () => {
        setStatus('loading')
        setAuthMessage('Authorizing your Microsoft account...')

        diagnosticContext({
            attemptID: createDiagnosticID(),
            companyID: null,
            employeeID: null,
            microsoftTenantID: null,
            surface: 'outlook',
            microsoftClientID: clientId,
            microsoftPermissionResource: outlookBackendScopes[0],
        })
        let stage = 'authentication_initialization'
        logDiagnostic('outlook_signin_request_started')
        try {
            await accountManager.initialize()

            setAuthMessage('Finishing secure sign-in...')
            stage = 'microsoft_token'
            const accessToken = await accountManager.ssoGetAccessToken(outlookBackendScopes)

            if (!accessToken) {
                throw new Error('No Microsoft access token was returned.')
            }

            const microsoftClaims = readMicrosoftTokenClaims(accessToken)

            diagnosticContext({ microsoftTenantID: microsoftClaims.tenantId })
            stage = 'backend_signin'
            setAuthMessage('Connecting to Yoffix...')
            const token = await loginToBackend(accessToken)

            const claims = readSessionClaims(token)

            diagnosticContext({ companyID: claims.companyId, employeeID: claims.employeeId })
            stage = 'company_bootstrap'
            setSessionToken(token)
            setSessionClaims(claims)
            setMicrosoftTenantId(microsoftClaims.tenantId)

            setAuthMessage('Loading offices and rooms...')
            const bootstrap = await fetchCompanyBootstrap(token, claims)

            const nextOffices = mapBootstrapOffices(bootstrap)

            logDiagnostic('outlook_bootstrap_dependency_resolved', { officeCount: nextOffices.length })
            if (!nextOffices.length) {
                logDiagnostic('outlook_signin_request_skipped', { reason: 'no_offices' }, 'WARN')
                throw new Error('No offices available for this company.')
            }

            const nextInitialOfficeId =
                bootstrap.employee.favouriteOfficeID || bootstrap.employee.officeID || nextOffices[0].id

            setOffices(nextOffices)
            setInitialOfficeId(
                nextOffices.some((office) => office.id === nextInitialOfficeId)
                    ? nextInitialOfficeId
                    : nextOffices[0].id,
            )
            logDiagnostic('outlook_signin_request_succeeded', {
                officeID: nextOffices.some((office) => office.id === nextInitialOfficeId)
                    ? nextInitialOfficeId
                    : nextOffices[0].id,
            })
            setStatus('ready')
        } catch (error) {
            logDiagnostic('outlook_signin_request_failed', { stage, ...diagnosticError(error) }, 'ERROR')
            console.error(error)
            setStatus('error')
            setAuthMessage(error instanceof Error ? `Authorization failed. ${error.message}` : 'Authorization failed.')
        }
    }, [])

    useEffect(() => {
        void authorizeUser()
    }, [authorizeUser])

    return {
        status,
        authMessage,
        sessionClaims,
        microsoftTenantId,
        sessionToken,
        offices,
        initialOfficeId,
        retry: authorizeUser,
    }
}
